import * as React from 'react'

import { makeObservable, observable, runInAction } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { IPermitTypeField, ISignature } from '~/client/graph'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import IPermitFieldsStore from '~/client/src/shared/models/IPermitFieldsStore'
import { NO_VALUE } from '~/client/src/shared/utils/usefulStrings'

import StruxhubInputHelperText from '../../../StruxhubInputs/components/StruxhubInputHelperText'
import StruxhubInputLabel from '../../../StruxhubInputs/components/StruxhubInputLabel'
import SitePermitCreationFormStore from '../../WorkflowCreationForm.store'
import SignaturePreview from './components/SignaturePreview'
import PermitBaseFormField from './PermitBaseFormField'
import PermitFieldRemoveIcon from './PermitFieldRemoveIcon'

import './PermitFormSignatureField.scss'

interface IProps {
  typeField: IPermitTypeField

  store: SitePermitCreationFormStore
  fieldsStore: IPermitFieldsStore

  isViewMode?: boolean

  tableId?: string
  tableRowIndex?: number
  parentInstanceId?: string
  parentValueIndex?: number
}

@observer
export default class PermitFormSignatureField extends React.Component<IProps> {
  @observable private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)
    this.localProps = props
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps !== this.props) {
      runInAction(() => {
        this.localProps = this.props
      })
    }
  }

  public render(): JSX.Element {
    const {
      isViewMode,
      typeField,
      store,
      fieldsStore,
      parentInstanceId,
      parentValueIndex,
    } = this.localProps

    return (
      <PermitBaseFormField
        typeField={typeField}
        fieldValues={this.fieldValues}
        isViewMode={isViewMode}
        parentInstanceId={parentInstanceId}
        parentValueIndex={parentValueIndex}
        store={store}
        fieldsStore={fieldsStore}
        componentRenderer={this.renderComponent}
        viewModeRenderer={this.renderViewMode}
        onAddFieldClick={this.addNewSignature}
        onRemoveField={this.removeSignature}
      />
    )
  }

  private renderComponent = (
    value: ISignature,
    index: number,
    isHeaderShown?: boolean,
  ): JSX.Element => {
    const { typeField } = this.localProps
    const { caption, isMandatory, isMultiple } = typeField
    const shouldShowRemoveBtn = !!index || !!value?.dataUrl

    const isLabelShown = !index || isHeaderShown

    return (
      <div
        key={index}
        className={classList({
          'signature-field-value-container': true,
          // This field renders StruxhubInputLabel directly rather than through
          // BaseStruxhubInput, so it has to reproduce its chrome: mt8 above the
          // label, and the helper-text row below.
          'tw:mt-2': isLabelShown,
        })}
      >
        {isLabelShown && (
          <StruxhubInputLabel
            label={caption}
            isRequired={isMandatory}
            value={value?.dataUrl || ''}
            isValid={this.isValidAtIndex(index)}
          />
        )}
        <div className="row y-start">
          <div className="col full-width">
            {this.renderEditModeForIndex(value, index)}
          </div>
          {isMultiple && shouldShowRemoveBtn && !isHeaderShown && (
            <PermitFieldRemoveIcon
              index={index}
              onClick={this.removeSignature}
            />
          )}
        </div>
        {index === this.fieldValues.length - 1 && (
          <StruxhubInputHelperText
            isChanged={false}
            isValid={this.isValidAtIndex(index)}
          />
        )}
      </div>
    )
  }

  private renderEditModeForIndex(
    signature: ISignature,
    index: number,
  ): JSX.Element {
    const { typeField } = this.localProps

    if (signature?.dataUrl) {
      return (
        <div className="signature-field-container mt8">
          <SignaturePreview
            signature={signature}
            onClick={this.openModalForIndex.bind(this, index)}
            isEditable
            isUserStampEnabled={typeField.isUserStampEnabled}
          />
          <button
            type="button"
            className="signature-clear-field-btn mt8"
            onClick={this.clearSignatureAtIndex.bind(this, index)}
          >
            {Localization.translator.clear}
          </button>
        </div>
      )
    }

    return (
      <div
        className="signature-field-placeholder mt8"
        onClick={this.openModalForIndex.bind(this, index)}
      >
        <span className="text light">
          {Localization.translator.clickToAddSignature}
        </span>
      </div>
    )
  }

  private renderViewMode = (value: ISignature): JSX.Element => {
    const { typeField } = this.localProps

    if (value?.dataUrl) {
      return (
        <SignaturePreview
          signature={value}
          isUserStampEnabled={typeField.isUserStampEnabled}
        />
      )
    }
    return <div className="text large word-break-all">{NO_VALUE}</div>
  }

  private get isTableField(): boolean {
    return !!this.localProps.tableId
  }

  private isValidAtIndex(index: number): boolean {
    const {
      typeField,
      fieldsStore,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.localProps
    return fieldsStore.isFieldValid(
      typeField,
      index,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    )
  }

  private get fieldValues(): ISignature[] {
    const {
      typeField,
      fieldsStore,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.localProps
    const values = fieldsStore.getFieldValues<ISignature>(
      typeField.id,
      tableId,
      tableRowIndex,
      false,
      parentInstanceId,
      parentValueIndex,
    )

    return values?.length ? values : [null]
  }

  private openModalForIndex(index: number) {
    const {
      store,
      fieldsStore,
      typeField,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.localProps
    store.showSignatureModal(
      typeField,
      fieldsStore,
      index,
      this.fieldValues[index],
      parentInstanceId,
      parentValueIndex,
      tableId,
      tableRowIndex,
    )
  }

  private clearSignatureAtIndex(index: number) {
    const {
      fieldsStore,
      typeField,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.localProps

    if (this.isTableField) {
      fieldsStore.changeTableFieldValue(
        tableId,
        tableRowIndex,
        typeField,
        index,
        null,
        parentInstanceId,
        parentValueIndex,
      )
      return
    }

    fieldsStore.changeFieldValue(
      typeField,
      index,
      null,
      parentInstanceId,
      parentValueIndex,
    )
  }

  private addNewSignature = () => {
    if (!this.isTableField) {
      const { fieldsStore, typeField, parentInstanceId, parentValueIndex } =
        this.localProps
      fieldsStore.addNewFieldValue(
        typeField,
        null,
        parentInstanceId,
        parentValueIndex,
      )
    }
  }

  private removeSignature = (index: number) => {
    if (!this.isTableField) {
      const { fieldsStore, typeField, parentInstanceId, parentValueIndex } =
        this.localProps
      fieldsStore.removeFieldValue(
        typeField.id,
        index,
        parentInstanceId,
        parentValueIndex,
      )
    }
  }
}
